import React, { useEffect, useState, useCallback } from 'react';
import { useConnectionStatus } from '../../hooks/useConnectionStatus';
import { ConnectionStatusStore } from '../../stores/connection-status.store';

const CONNECTED_LINGER_MS = 3000;

export default function ConnectionStatusBanner() {
  const { isReachable, lostAt, reconnecting, nextRetryAt } = useConnectionStatus();
  const [visible, setVisible] = useState(false);
  const [showConnected, setShowConnected] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [retryInSec, setRetryInSec] = useState(null);

  useEffect(() => {
    if (!isReachable) {
      setShowConnected(false);
      setVisible(true);
      return;
    }

    if (visible) {
      setShowConnected(true);
      const timer = setTimeout(() => {
        setVisible(false);
        setShowConnected(false);
      }, CONNECTED_LINGER_MS);
      return () => clearTimeout(timer);
    }
  }, [isReachable]);

  useEffect(() => {
    if (isReachable || !visible) {
      setElapsedSec(0);
      setRetryInSec(null);
      return;
    }

    const interval = setInterval(() => {
      const nowMs = Date.now();
      const elapsed = lostAt ? Math.floor((nowMs - lostAt) / 1000) : 0;
      setElapsedSec(elapsed);

      if (nextRetryAt !== null) {
        const remaining = Math.max(0, Math.ceil((nextRetryAt - nowMs) / 1000));
        setRetryInSec(remaining);
      } else {
        setRetryInSec(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isReachable, visible, lostAt, nextRetryAt]);

  const handleForceReconnect = useCallback(() => {
    ConnectionStatusStore.forceReconnectCheck();
  }, []);

  if (!visible) return null;

  if (showConnected) {
    return (
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 text-white text-sm font-medium shadow-md">
        <svg className="w-4 h-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
        Back online
      </div>
    );
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-3 px-4 py-2 bg-amber-500 text-white text-sm font-medium shadow-md">
      <svg
        className="w-4 h-4 shrink-0 animate-spin"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
        />
      </svg>

      <span className="flex items-center gap-1.5">
        <span className="font-semibold">Connection lost</span>
        {elapsedSec > 0 && (
          <span className="opacity-80 font-normal">
            &mdash; {elapsedSec}s ago
          </span>
        )}
      </span>

      {!reconnecting && retryInSec !== null && (
        <span className="opacity-75 font-normal text-xs">
          Retrying in {retryInSec}s
        </span>
      )}

      <button
        onClick={handleForceReconnect}
        disabled={reconnecting}
        className="ml-2 flex items-center gap-1.5 px-3 py-1 rounded bg-surface bg-opacity-20 hover:bg-opacity-30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-semibold"
      >
        {reconnecting ? (
          <>
            <svg
              className="w-3 h-3 animate-spin shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            Checking...
          </>
        ) : (
          'Reconnect now'
        )}
      </button>
    </div>
  );
}
